import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Loading,
  Toast,
} from '@/components/ui';

/**
 * `core show channels`, annotated, from the browser.
 *
 * The arrivals board answers "what did the bot receive". This answers the
 * question underneath it: the dialer shows these calls and the platform shows
 * nothing — where are they?
 *
 * Nine numbers were reported stuck. Eight appeared nowhere in twenty-five
 * thousand lines of log, because they never entered the Stasis application at
 * all — up on the switch, on a dialplan path that never reached the bot. Every
 * other screen reads rows this platform created, so a channel that never
 * arrived is invisible on all of them.
 */

interface Channel {
  channel_id: string;
  name: string | null;
  state: string | null;
  caller: string | null;
  connected: string | null;
  context: string | null;
  exten: string | null;
  application: string | null;
  age_sec: number | null;
  in_our_stasis_app: boolean;
  call_id: string | null;
  client_name: string | null;
  verdict: string;
}

interface Channels {
  ok: boolean;
  error?: string;
  data: Channel[];
  count: number;
  ours: number;
  never_reached_us: number;
  stasis_app: string;
  hint: string;
}

const TONES: Record<string, 'green' | 'amber' | 'red'> = {
  'live on this platform': 'green',
  'in our app, no live call row': 'amber',
  'never reached the bot': 'red',
};

function age(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export default function AsteriskChannels() {
  const channels = useQuery({
    queryKey: ['asterisk-channels'],
    queryFn: () => api.get<Channels>('/admin/asterisk-channels'),
    // The switch changes second to second and this is a live view of it.
    refetchInterval: 10_000,
  });

  const [ending, setEnding] = useState<Channel | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  /**
   * End one channel, by name.
   *
   * The orphan sweep is deliberately conservative — it waits out a grace
   * period and only claims channels in this deployment's own Stasis app,
   * because a reaper that kills live calls is far worse than the leak it
   * fixes. That leaves a real gap, and this fills it: a channel an operator
   * can see is stuck right now, that nothing automatic will claim, or that
   * they do not want to wait five minutes for.
   *
   * One at a time and never in bulk. Deciding what is stuck is the judgement
   * a person is here for; the sweep already owns every case a machine can
   * settle on its own.
   */
  const hangup = useMutation({
    mutationFn: (channelId: string) =>
      api.post<{ ended: string[]; failed: Record<string, string> }>(
        '/admin/asterisk-channels/hangup',
        { channel_ids: [channelId], reason: 'operator' },
      ),
    onSuccess: (result, channelId) => {
      void channels.refetch();
      setEnding(null);
      setToast(
        result.ended.length
          ? { message: 'Channel ended.', tone: 'good' }
          : {
              message:
                result.failed[channelId] ??
                'The switch would not end it — it may already be gone.',
              tone: 'bad',
            },
      );
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not end it'), tone: 'bad' }),
  });

  if (channels.isPending) return <Loading />;
  if (channels.error)
    return <ErrorState error={channels.error} retry={() => channels.refetch()} />;

  const { ok, error, data, count, ours, never_reached_us: stranded, hint } =
    channels.data;

  return (
    <Card
      title="Channels on the switch"
      description="Everything Asterisk is holding right now, whether or not it ever reached the bot."
      padded={false}
    >
      {!ok ? (
        <div className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
          Could not reach Asterisk over ARI: {error}. That is itself the finding
          if calls are not arriving.
        </div>
      ) : (
        <div className="border-b border-line px-4 py-3 text-sm">
          {stranded ? (
            <span className="text-red-700 dark:text-red-400">
              <strong className="tabular-nums">{count}</strong> channels up,{' '}
              <strong className="tabular-nums">{ours}</strong> in the bot's app,
              and <strong className="tabular-nums">{stranded}</strong> that never
              reached it.
            </span>
          ) : (
            <span className="text-ink-600 dark:text-ink-300">
              <strong className="tabular-nums">{count}</strong> channels up, and
              every one of them reached the bot.
            </span>
          )}
        </div>
      )}

      {!data.length ? (
        <EmptyState title="Nothing on the switch" description={hint} />
      ) : (
        <div className="overflow-x-auto">
          <table className="glee-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Number</th>
                <th>Age</th>
                <th>Where it is</th>
                <th>Verdict</th>
                <th className="text-right">End it</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.channel_id}
                  className={
                    row.verdict === 'never reached the bot'
                      ? 'bg-red-50/50 dark:bg-red-950/20'
                      : undefined
                  }
                >
                  <td className="whitespace-nowrap text-xs text-ink-500">
                    {row.name ?? row.channel_id}
                  </td>
                  <td className="whitespace-nowrap tabular-nums">
                    {row.caller ?? row.connected ?? '—'}
                  </td>
                  <td className="whitespace-nowrap tabular-nums text-ink-600 dark:text-ink-300">
                    {age(row.age_sec)}
                  </td>
                  <td className="text-xs text-ink-600 dark:text-ink-300">
                    {/* The dialplan position is the whole answer for a stranded
                        channel: it says which step it never got past. */}
                    {row.application ?? '—'}
                    {row.context ? (
                      <span className="text-ink-400">
                        {' · '}
                        {row.context}
                        {row.exten ? `,${row.exten}` : ''}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <Badge tone={TONES[row.verdict] ?? 'gray'}>{row.verdict}</Badge>
                  </td>
                  <td className="text-right">
                    {/* Offered on every row, including live ones. An operator
                        looking at this page is usually looking BECAUSE
                        something is wrong, and a button that hides itself on
                        the row they need is worse than one they have to read
                        the confirmation on. */}
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={hangup.isPending && hangup.variables === row.channel_id}
                      onClick={() => setEnding(row)}
                    >
                      Hang up
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ok && stranded ? (
        <p className="border-t border-line px-4 py-3 text-xs text-ink-500">{hint}</p>
      ) : null}

      <ConfirmDialog
        open={!!ending}
        title="End this channel?"
        confirmLabel="Hang up"
        busy={hangup.isPending}
        onCancel={() => setEnding(null)}
        onConfirm={() => ending && hangup.mutate(ending.channel_id)}
        body={
          <div className="space-y-2 text-sm">
            <p>
              <strong>{ending?.caller ?? ending?.name ?? ending?.channel_id}</strong>
              {ending?.age_sec ? ` — up for ${age(ending.age_sec)}` : null}
            </p>
            <p>
              {ending?.verdict === 'live on this platform'
                ? 'This one is a LIVE call with a session behind it. Ending it drops whoever is on the line.'
                : 'Nothing on this platform is holding this channel, so nothing else will ever end it.'}
            </p>
            <p className="text-ink-500">
              The carrier stops being billed for it the moment it goes. Its call
              row, if it has one, is closed and the lead goes back to the dialer
              for another attempt rather than being retired.
            </p>
          </div>
        }
      />

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}
